/** The version numbers one committed item took. */
export interface VersionRange {
  firstVersion: number;
  lastVersion: number;
}

/**
 * Commit items as consecutive versions of one document, strictly in order.
 *
 * Every version number is derived server-side from `max(version) + 1` under an
 * advisory lock — for an upload and for an existing document moved or copied
 * in alike — so the *only* thing that ties an item to its numbers is the order
 * it is issued in. Awaiting each item before starting the next is therefore a
 * correctness requirement, not a throttle: running them concurrently would
 * still be safe, but would assign version numbers in arbitrary order.
 *
 * Stops at the first failure rather than stacking the remaining items onto a
 * version chain with a hole in it. Whatever landed before the failure is
 * already committed and is reported back so the caller can say so — and keep
 * the rest staged for a retry.
 */
export async function runVersionItemsInOrder<T>(
  items: readonly T[],
  commit: (item: T) => Promise<VersionRange | null>
): Promise<{ ranges: VersionRange[]; failedItem: T | null }> {
  const ranges: VersionRange[] = [];

  for (const item of items) {
    const range = await commit(item);
    if (!range) {
      return { ranges, failedItem: item };
    }
    ranges.push(range);
  }

  return { ranges, failedItem: null };
}
